/**
 * The CSR home's Needs attention panel (v1 completion I §10, the mock-up's
 * state 7): the caller's open alerts, worst first, up to ten, each opening its
 * sheet on the Needs attention page, and "See all (N)". Acknowledged alerts are
 * left off. The home shows it while `v1_completion` is on.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { ListResponse } from "@shared-core/data/adapter";
import type { NeedsAttentionItem } from "@shared-core/data/needs-attention-types";
import { EMPTY_CSR_HOME_PANEL } from "@shared-core/domain/needs-attention";
import { paths } from "@shared-core/routing/routes";
import { Card, CardContent } from "../../primitives/card";
import { Skeleton } from "../../primitives/skeleton";
import { DuePill, PriorityDot } from "./AlertDue";

const PANEL_SIZE = 10;

export function NeedsAttentionPanel() {
  const adapter = useDataAdapter();
  const [list, setList] = useState<ListResponse<NeedsAttentionItem> | null>(
    null,
  );
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    adapter
      .listNeedsAttention({
        mine: true,
        state: ["open"],
        sort: "priority,due_at",
        pageSize: PANEL_SIZE,
      })
      .then((found) => {
        if (!cancelled) setList(found);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [adapter]);

  return (
    <Card>
      <CardContent className="space-y-2 py-4">
        <div className="flex items-baseline justify-between gap-4">
          <h2 className="text-base font-semibold">Needs attention</h2>
          {list && list.total > 0 && (
            <Link
              className="text-sm text-primary underline"
              to={paths.needsAttention()}
            >
              See all ({list.total})
            </Link>
          )}
        </div>
        {failed ? (
          <p className="text-sm text-muted-foreground">
            Needs attention did not load.
          </p>
        ) : list === null ? (
          <div className="space-y-2" aria-busy="true">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : list.total === 0 ? (
          <p className="text-sm text-muted-foreground">
            {EMPTY_CSR_HOME_PANEL}
          </p>
        ) : (
          <ul className="divide-y divide-border">
            {list.items.map((alert) => (
              <li key={alert.id}>
                <Link
                  to={paths.needsAttention({ alert: alert.id })}
                  className="flex items-start justify-between gap-3 py-2 hover:bg-muted/40"
                >
                  <span className="flex items-start text-sm">
                    <PriorityDot priority={alert.priority} />
                    <span>
                      {alert.headline} ·{" "}
                      <span className="font-mono text-xs">
                        {alert.subject_ref}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {alert.customer_name ?? "Stock"}
                      </span>
                    </span>
                  </span>
                  <DuePill alert={alert} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
